'use client';

import { Box, VStack } from '@terra/ui-styled-system';
import { BottomTabBar } from './BottomTabBar';
import { TerminalContent } from './TerminalContent';
import { useTerminalTabActions } from './useTerminalTabActions';
import { CodeGraphContent } from '../../tabs/CodeGraphContent';
import { DecoderContent } from '../../decoder/DecoderContent';
import type { BottomTab, BottomPanelProps, TabActions } from './types';

function TabContent({
  tab,
  selectedEndpoint,
  endpointId,
}: {
  tab: BottomTab;
  selectedEndpoint?: {
    method: string;
    path: string;
    domain: string;
  };
  endpointId?: string;
}) {
  if (tab.content) {
    return tab.content;
  }

  switch (tab.type) {
    case 'terminal':
      return null;
    case 'code-graph':
      return (
        <CodeGraphContent
          endpointId={endpointId}
          selectedEndpoint={selectedEndpoint}
        />
      );
    case 'decoder':
      return <DecoderContent />;
    default:
      return null;
  }
}

function isPersistentTerminalTab(tab: BottomTab): boolean {
  return tab.type === 'terminal' && !tab.content;
}

function useTabActions(activeTabType?: string): TabActions | undefined {
  const terminalActions = useTerminalTabActions();

  if (activeTabType === 'terminal') {
    return terminalActions;
  }

  return undefined;
}

export function BottomPanel({
  tabs,
  activeTab,
  onTabSelect,
  onToggleCollapse,
  isCollapsed,
  selectedEndpoint,
  endpointId,
}: BottomPanelProps) {
  const tabActions = useTabActions(activeTab?.type);
  const hasTerminalTab = tabs.some(isPersistentTerminalTab);
  const isTerminalActive =
    activeTab !== undefined && isPersistentTerminalTab(activeTab);

  return (
    <VStack
      gap={0}
      h="full"
      justifyContent={isCollapsed ? 'flex-end' : 'flex-start'}
      alignItems="stretch"
      width="100%"
    >
      <BottomTabBar
        tabs={tabs}
        onTabSelect={onTabSelect}
        onToggleCollapse={onToggleCollapse}
        isCollapsed={isCollapsed}
        tabActions={tabActions}
      />

      <Box
        flex="1"
        overflow="hidden"
        display={isCollapsed ? 'none' : undefined}
      >
        {hasTerminalTab && (
          <Box
            h="full"
            w="full"
            display={isTerminalActive ? undefined : 'none'}
          >
            <TerminalContent />
          </Box>
        )}
        {activeTab && !isTerminalActive && (
          <TabContent
            tab={activeTab}
            selectedEndpoint={selectedEndpoint}
            endpointId={endpointId}
          />
        )}
      </Box>
    </VStack>
  );
}
